import { zodResolver } from '@hookform/resolvers/zod';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { TICKET_CATEGORIES, ticketSchema, type TicketInput, type TicketValues } from '@sf/shared';
import { useAuth } from '../../app/authContext';
import { ImageUploader } from '../../components/ImageUploader';
import { Button } from '../../components/ui/Button';
import { Card, PageHeader } from '../../components/ui/Feedback';
import { SelectField, TextAreaField, TextField } from '../../components/ui/Field';
import { useToast } from '../../components/ui/Toast';
import { t } from '../../lib/copy';
import { messageForError } from '../../lib/errors';
import { compressImage, ImageError } from '../../lib/imageCompress';
import { useOnline } from '../../lib/useOnline';
import { createTicket, setTicketImages, uploadTicketImage } from './api';

export function RaiseTicketPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const online = useOnline();
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<number[] | null>(null);
  const [stage, setStage] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm<TicketInput, unknown, TicketValues>({
    resolver: zodResolver(ticketSchema),
    defaultValues: { category: '' as TicketInput['category'], title: '', description: '' },
  });
  const isOther = useWatch({ control, name: 'category' }) === 'other';

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    if (!online) {
      setFormError(t('net.offline'));
      return;
    }
    // Compress first so nothing is created if a photo can't be used.
    let prepared: File[];
    try {
      setStage(t('loading.generic'));
      prepared = await Promise.all(files.map(compressImage));
    } catch (err) {
      setStage(null);
      const reason = err instanceof ImageError ? err.reason : 'unreadable';
      setFormError(
        reason === 'tooLarge' ? t('ticket.upload.tooLarge') : t('ticket.upload.badType'),
      );
      return;
    }

    try {
      const ticketId = await createTicket(profile!, values);
      if (prepared.length) {
        setProgress(prepared.map(() => 0));
        const done: string[] = [];
        let failed = false;
        for (const [i, file] of prepared.entries()) {
          setStage(t('ticket.upload.progress', { n: i + 1, total: prepared.length }));
          try {
            done.push(
              await uploadTicketImage(ticketId, file, (f) =>
                setProgress((p) => (p ? p.map((v, j) => (j === i ? f : v)) : p)),
              ),
            );
          } catch {
            failed = true;
          }
        }
        if (done.length) await setTicketImages(ticketId, done);
        if (failed) toast({ title: t('ticket.upload.failed'), variant: 'error' });
      }
      toast({ title: t('ticket.created'), variant: 'success' });
      navigate(`/app/tickets/${ticketId}`, { replace: true });
    } catch (err) {
      setProgress(null);
      setStage(null);
      setFormError(messageForError(err));
    }
  });

  const busy = isSubmitting || !!progress;

  return (
    <div className="mx-auto max-w-2xl">
      <Link
        to="/app"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
      >
        <ChevronLeft className="size-4" aria-hidden />
        Back to my tickets
      </Link>
      <PageHeader title={t('ticket.new.title')} subtitle={t('ticket.new.descriptionHint')} />
      <Card>
        <form onSubmit={onSubmit} noValidate className="flex flex-col gap-5">
          {formError && (
            <div
              role="alert"
              className="rounded-input border border-danger-700/30 bg-danger-50 px-3 py-2 text-sm text-danger-700"
            >
              {formError}
            </div>
          )}
          <SelectField
            label="Category"
            required
            error={errors.category?.message}
            disabled={busy}
            {...register('category')}
          >
            <option value="" disabled>
              Choose a category
            </option>
            {TICKET_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </SelectField>
          {isOther && (
            <TextField
              label="Short title"
              required
              maxLength={80}
              error={errors.title?.message}
              disabled={busy}
              {...register('title')}
            />
          )}
          <TextAreaField
            label="Description"
            required
            rows={5}
            error={errors.description?.message}
            disabled={busy}
            {...register('description')}
          />
          <ImageUploader files={files} onChange={setFiles} progress={progress} disabled={busy} />
          <div className="flex flex-wrap items-center justify-end gap-3">
            {stage && (
              <span role="status" className="text-sm text-slate">
                {stage}
              </span>
            )}
            <Button type="submit" loading={busy}>
              Submit ticket
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
